import { Popover, PopoverContent, PopoverTrigger } from '@ria-client/tw-shadcn/components/popover';
import { ChevronDown } from 'lucide-react';
import { useId } from 'react';
import { useTrackedElement } from './use-tracked-element';

export interface FeeTier {
  label?: string;
  amount?: number;
}

const usd = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 0,
  style: 'currency',
});

export function FeeDetails({ tiers, footnote }: { tiers: FeeTier[]; footnote?: string }) {
  const rows = tiers.filter(
    (tier): tier is Required<FeeTier> => Boolean(tier.label?.trim()) && tier.amount != null,
  );
  const { ref, track } = useTrackedElement('advisor_product_card_fee_details');
  const titleId = useId();

  if (!rows.length) return null;

  return (
    <Popover onOpenChange={open => open && track('open')}>
      <PopoverTrigger
        className="group mt-0.5 inline-flex items-center gap-0.5 rounded bg-transparent p-0 text-xs font-semibold text-[var(--content-green)] transition-colors hover:text-[var(--content-green-dark)] @xl:mt-1"
        ref={ref}
      >
        Fee details
        <ChevronDown
          aria-hidden
          className="size-3.5 transition-transform group-data-[state=open]:rotate-180"
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-labelledby={titleId}
        className="w-72 rounded-lg border-border p-0 text-left"
      >
        <div className="flex items-baseline justify-between border-b border-border px-4 py-2.5">
          <p className="m-0 text-[11px] font-semibold tracking-wider uppercase" id={titleId}>
            Fee details
          </p>
          <p className="m-0 text-[11px] text-muted-foreground">By plan tier</p>
        </div>
        <dl className="m-0 flex flex-col divide-y divide-border px-4">
          {rows.map(row => (
            <div className="flex items-baseline justify-between gap-4 py-2" key={row.label}>
              <dt className="text-[13px]">{row.label}</dt>
              <dd className="m-0 text-right text-sm font-bold">{usd.format(row.amount)}/yr</dd>
            </div>
          ))}
        </dl>
        {footnote?.trim() ? (
          <p className="m-0 border-t border-border px-4 py-2 text-[11px] leading-4 text-muted-foreground">
            {footnote}
          </p>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
